import React, { useState, useMemo } from 'react';
import { Card, ProgressBar, Spinner, Button, Collapse } from 'react-bootstrap';
import type { DailyMissionsToday, DailyMissionTodayItem } from '../services/api';
import InventoryPotionSprite, { inventoryPotionGridMeta } from './InventoryPotionSprite';

export type DailyMissionSafeT = (key: string, fallback: string) => string;

export type DailyMissionsCardVariant = 'desktop' | 'mobile';

const kindsOrder = ['SoloPlays', 'PvpPlays', 'PvPRoomsOpened', 'PerfectQuizzes'] as const;

function sortMissions(missions: DailyMissionTodayItem[]): DailyMissionTodayItem[] {
  const idx = (k: string) => {
    const i = kindsOrder.indexOf(k as (typeof kindsOrder)[number]);
    return i === -1 ? 99 : i;
  };
  return [...missions].sort((a, b) => idx(a.kind) - idx(b.kind));
}

export interface DailyMissionsSidebarCardProps {
  /** Desktop: sidebar strip. Mobile: collapsible block under level/XP (starts collapsed). */
  variant?: DailyMissionsCardVariant;
  data: DailyMissionsToday | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  safeT: DailyMissionSafeT;
  itemName: (itemTypeKey: string) => string;
  itemShortName: (itemTypeKey: string) => string;
}

const DailyMissionsSidebarCard: React.FC<DailyMissionsSidebarCardProps> = ({
  variant = 'desktop',
  data,
  loading,
  error,
  onRetry,
  safeT,
  itemName,
  itemShortName,
}) => {
  const isMobile = variant === 'mobile';
  const [mobilePanelOpen, setMobilePanelOpen] = useState(false);

  const missions = useMemo(() => (data?.missions?.length ? sortMissions(data.missions) : []), [data]);

  const rewardVisuals = (m: DailyMissionTodayItem) => {
    if (!m.rewards?.length) {
      return <span className="daily-missions-reward-fallback">{safeT('dailyMissions.rewardNone', '—')}</span>;
    }

    return (
      <span className="daily-missions-reward-chips">
        {m.rewards.map((r, idx) => {
          const key = `${m.kind}-${idx}-${r.rewardType}`;
          if (r.rewardType === 'Xp' && r.xpAmount != null) {
            return (
              <span key={key} className="daily-missions-reward-chip daily-missions-reward-chip--xp">
                <span className="daily-missions-reward-chip-ico" aria-hidden>
                  <i className="bi bi-lightning-charge-fill daily-missions-reward-xp-ico" />
                </span>
                <span className="daily-missions-reward-chip-label">+{r.xpAmount} XP</span>
              </span>
            );
          }
          if (r.rewardType === 'InventoryItem' && r.itemTypeKey) {
            const n = Math.max(1, r.itemQuantity || 1);
            const fullLabel = itemName(r.itemTypeKey);
            const label = itemShortName(r.itemTypeKey);
            const meta = inventoryPotionGridMeta(r.itemTypeKey);
            return (
              <span
                key={key}
                className="daily-missions-reward-chip daily-missions-reward-chip--item"
                title={n > 1 ? `${n}× ${fullLabel}` : fullLabel}
              >
                <span className="daily-missions-reward-chip-ico" aria-hidden>
                  {meta ? (
                    <InventoryPotionSprite
                      variant={meta.variant}
                      size={meta.size}
                      frameDisplayPx={26}
                      className="daily-missions-potion-sprite"
                    />
                  ) : (
                    <i className="bi bi-box-seam daily-missions-reward-fallback-ico" />
                  )}
                </span>
                <span className="daily-missions-reward-chip-label">
                  {n > 1 ? `${n}× ${label}` : label}
                </span>
              </span>
            );
          }
          return null;
        })}
      </span>
    );
  };

  const missionList = (
    <ul className="list-unstyled mb-0 daily-missions-sidebar-list">
      {missions.map((m) => {
        const target = Math.max(1, m.targetCount);
        const prog = Math.min(m.progress, target);
        const pct = target > 0 ? Math.min(100, (prog / target) * 100) : 0;
        const expanded = true;

        const missionTitle = (
          <div className="daily-missions-sidebar-row__head w-100">
            <div className="daily-missions-sidebar-row__mission-title-stack">
              <span className="small fw-semibold text-body daily-missions-sidebar-row__title">
                {safeT(`dailyMissions.kind.${m.kind}`, m.kind)}
              </span>
              {m.completed ? (
                <span className="daily-missions-sidebar-done small text-success">{safeT('dailyMissions.completed', 'Done')}</span>
              ) : null}
            </div>
          </div>
        );

        const missionBar = (
          <div className="daily-missions-sidebar-row__bar-wrap">
            <ProgressBar
              now={pct}
              variant={m.completed ? 'success' : 'info'}
              className="daily-missions-sidebar-row__bar"
              visuallyHidden
              aria-label={safeT('dailyMissions.progressAria', 'Progress')}
            />
            <span className="daily-missions-sidebar-row__meta small text-muted text-nowrap">{prog}/{target}</span>
          </div>
        );

        const rewardsLabel = (
          <div className="fw-semibold text-secondary daily-missions-sidebar-row__rewards-label">
            {safeT('dailyMissions.rewards', 'Rewards')}
          </div>
        );

        return (
          <li
            key={m.kind}
            className={`daily-missions-sidebar-row mb-2${
              isMobile && expanded ? ' daily-missions-sidebar-row--mobile-expanded' : ''
            }`}
          >
            <div className="daily-missions-sidebar-row__grid daily-missions-sidebar-row__grid--always-expanded">
              <div className="daily-missions-sidebar-row__grid-mission">
                {missionTitle}
                {missionBar}
              </div>
              <div className="daily-missions-sidebar-row__grid-rewards-panel">
                <div className="daily-missions-sidebar-rewards-panel">
                  {rewardsLabel}
                  <div className="small text-muted px-0 daily-missions-sidebar-rewards">{rewardVisuals(m)}</div>
                </div>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );

  const bodyContent =
    loading ? (
      <div className="text-center py-2" role="status" aria-live="polite">
        <Spinner animation="border" size="sm" />
      </div>
    ) : error ? (
      <div className="px-1">
        <p className="small text-warning mb-2">{safeT('dailyMissions.loadError', 'Could not load daily missions.')}</p>
        <Button variant="link" size="sm" className="p-0 small" onClick={onRetry}>
          {safeT('dailyMissions.retry', 'Try again')}
        </Button>
      </div>
    ) : missions.length === 0 ? (
      <small className="text-muted">{safeT('dailyMissions.empty', 'No missions')}</small>
    ) : (
      missionList
    );

  const cardClass = 'top-players-card-sidebar daily-missions-sidebar-card';

  if (isMobile) {
    const panelId = 'daily-missions-mobile-panel';
    return (
      <div className="daily-missions-mobile-wrap d-lg-none mt-2">
        <Card className={cardClass}>
          <Card.Header className="p-0 border-bottom-0 bg-white">
            <button
              type="button"
              id="daily-missions-mobile-toggle"
              className="daily-missions-mobile-toggle btn btn-link w-100 text-decoration-none text-body px-3 py-2 d-flex justify-content-between align-items-center"
              onClick={() => setMobilePanelOpen((o) => !o)}
              aria-expanded={mobilePanelOpen}
              aria-controls={panelId}
              aria-label={safeT('dailyMissions.mobileToggleAria', 'Show or hide daily missions')}
            >
              <span className="d-inline-flex align-items-center gap-2">
                <small className="fw-bold">{safeT('dailyMissions.title', 'Daily missions')}</small>
                {loading ? <Spinner animation="border" size="sm" className="daily-missions-mobile-toggle-spinner" /> : null}
              </span>
              <i className={`bi ${mobilePanelOpen ? 'bi-chevron-up' : 'bi-chevron-down'} text-muted`} aria-hidden />
            </button>
          </Card.Header>
          <Collapse in={mobilePanelOpen}>
            <div id={panelId}>
              <Card.Body className="p-2 pt-2 border-top daily-missions-mobile-body">{bodyContent}</Card.Body>
            </div>
          </Collapse>
        </Card>
      </div>
    );
  }

  return (
    <div className="daily-missions-sidebar mb-2 d-none d-lg-block">
      <Card className={cardClass}>
        <Card.Header className="p-2 text-center">
          <small className="fw-bold">{safeT('dailyMissions.title', 'Daily missions')}</small>
        </Card.Header>
        <Card.Body className="p-2">{bodyContent}</Card.Body>
      </Card>
    </div>
  );
};

export default DailyMissionsSidebarCard;
